/**
 * Audit & Security Monitoring API
 * Wraps /api/v1/security/{alerts,audit-logs,rate-violations,metrics}/
 * (admin-only: IsAdminUser permission on every endpoint).
 */
import apiBase, { PaginatedResponse } from './api-base';

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical';
export type AlertStatus = 'open' | 'acknowledged' | 'investigating' | 'resolved' | 'false_positive';

export interface SecurityAlertListItem {
  id: number;
  created_at: string;
  alert_type: string;
  alert_type_display: string;
  severity: AlertSeverity;
  severity_display: string;
  status: AlertStatus;
  status_display: string;
  title: string;
  ip_address: string | null;
  assigned_to_username: string | null;
  event_count: number;
}

export interface SecurityAlert extends SecurityAlertListItem {
  updated_at: string;
  resolved_at: string | null;
  description: string;
  details: Record<string, unknown>;
  user: number | null;
  user_username: string | null;
  assigned_to: number | null;
  resolution_notes: string;
  first_seen: string;
  last_seen: string;
  is_active: boolean;
  time_to_resolution: number | null;
}

export interface SecurityAuditLog {
  id: number;
  timestamp: string;
  event_type: string;
  event_type_display: string;
  severity: AlertSeverity;
  severity_display: string;
  username: string | null;
  ip_address: string | null;
  user_agent: string;
  request_path: string;
  request_method: string;
  details: Record<string, unknown>;
  hash_chain: string;
  previous_hash: string;
}

export interface RateLimitViolation {
  id: number;
  timestamp: string;
  ip_address: string;
  user: number | null;
  user_username: string | null;
  endpoint: string;
  method: string;
  violation_count: number;
  limit_type: string;
  blocked_until: string | null;
  is_blocked: boolean;
  is_currently_blocked: boolean;
  details: Record<string, unknown>;
  user_agent: string;
}

export interface AlertStatistics {
  total: number;
  by_severity: Record<string, number>;
  by_status: Record<string, number>;
  by_type: Record<string, number>;
  resolved_count?: number;
  open_count?: number;
  avg_resolution_time?: number;
}

export interface IntegrityVerification {
  valid: boolean;
  verified_count: number;
  errors: string[];
}

export interface AlertFilters {
  severity?: AlertSeverity;
  status?: AlertStatus;
  alert_type?: string;
  assigned_to?: number;
  ip_address?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

export interface AuditLogFilters {
  event_type?: string;
  severity?: AlertSeverity;
  username?: string;
  ip_address?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

export interface RateViolationFilters {
  ip_address?: string;
  endpoint?: string;
  is_blocked?: boolean;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
}

const BASE = '/security';

export const auditAPI = {
  // ───── Alerts ─────
  listAlerts: async (filters: AlertFilters = {}): Promise<PaginatedResponse<SecurityAlertListItem>> => {
    const { data } = await apiBase.get(`${BASE}/alerts/`, { params: filters });
    return data;
  },
  getAlert: async (id: number): Promise<SecurityAlert> => {
    const { data } = await apiBase.get(`${BASE}/alerts/${id}/`);
    return data;
  },
  activeAlerts: async (): Promise<SecurityAlertListItem[]> => {
    const { data } = await apiBase.get(`${BASE}/alerts/active/`);
    return Array.isArray(data) ? data : data.results ?? [];
  },
  criticalAlerts: async (): Promise<SecurityAlertListItem[]> => {
    const { data } = await apiBase.get(`${BASE}/alerts/critical/`);
    return Array.isArray(data) ? data : data.results ?? [];
  },
  alertStatistics: async (hours = 24): Promise<AlertStatistics> => {
    const { data } = await apiBase.get(`${BASE}/alerts/statistics/`, { params: { hours } });
    return data;
  },
  assignAlert: async (id: number, assignedTo: number, notes = ''): Promise<SecurityAlert> => {
    const { data } = await apiBase.post(`${BASE}/alerts/${id}/assign/`, {
      assigned_to: assignedTo,
      notes,
    });
    return data;
  },
  resolveAlert: async (id: number, notes = '', falsePositive = false): Promise<SecurityAlert> => {
    const { data } = await apiBase.post(`${BASE}/alerts/${id}/resolve/`, {
      notes,
      false_positive: falsePositive,
    });
    return data;
  },

  // ───── Audit Logs ─────
  listAuditLogs: async (filters: AuditLogFilters = {}): Promise<PaginatedResponse<SecurityAuditLog>> => {
    const { data } = await apiBase.get(`${BASE}/audit-logs/`, { params: filters });
    return data;
  },
  verifyIntegrity: async (startId?: number, endId?: number): Promise<IntegrityVerification> => {
    const params: Record<string, number> = {};
    if (startId) params.start_id = startId;
    if (endId) params.end_id = endId;
    const { data } = await apiBase.get(`${BASE}/audit-logs/verify_integrity/`, { params });
    return data;
  },

  // ───── Rate Limit Violations ─────
  listViolations: async (
    filters: RateViolationFilters = {},
  ): Promise<PaginatedResponse<RateLimitViolation>> => {
    const { data } = await apiBase.get(`${BASE}/rate-violations/`, { params: filters });
    return data;
  },
  blockedIps: async (): Promise<RateLimitViolation[]> => {
    const { data } = await apiBase.get(`${BASE}/rate-violations/blocked/`);
    return Array.isArray(data) ? data : data.results ?? [];
  },

  // ───── Metrics ─────
  authMetrics: async (hours = 24) => {
    const { data } = await apiBase.get(`${BASE}/metrics/authentication/`, { params: { hours } });
    return data;
  },
  authzMetrics: async (hours = 24) => {
    const { data } = await apiBase.get(`${BASE}/metrics/authorization/`, { params: { hours } });
    return data;
  },
  rateLimitMetrics: async (hours = 24) => {
    const { data } = await apiBase.get(`${BASE}/metrics/rate_limiting/`, { params: { hours } });
    return data;
  },
  alertMetrics: async (hours = 24) => {
    const { data } = await apiBase.get(`${BASE}/metrics/alerts/`, { params: { hours } });
    return data;
  },
  timeline: async (hours = 24, intervalHours = 1) => {
    const { data } = await apiBase.get(`${BASE}/metrics/timeline/`, {
      params: { hours, interval: intervalHours },
    });
    return data;
  },
  fullReport: async (hours = 24) => {
    const { data } = await apiBase.get(`${BASE}/metrics/report/`, { params: { hours } });
    return data;
  },
  runMonitoring: async () => {
    const { data } = await apiBase.post(`${BASE}/metrics/run_monitoring/`);
    return data;
  },
};

export default auditAPI;
